import { useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChart3, Download, Sparkles } from 'lucide-react';
import type { Posicao } from '@wms/shared';
import * as React from 'react';
import { useDashboard, useMapa, useSerieEmpresa } from '@/hooks/consultas';
import { useContexto } from '@/hooks/useContexto';
import { useParamUrl } from '@/hooks/useUrlEstado';
import { baixarCsv, carimboArquivo, type ColunaCsv } from '@/lib/csv';
import { compararNatural, dataCurta, numero, percentual } from '@/lib/utils';
import type { LinhaConsolidacao, PontoSerie, PontoSerieEmpresa } from '@/lib/tipos';
import { Botao } from '@/componentes/ui/botao';
import { Segmentado } from '@/componentes/ui/segmentado';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import {
  CabecalhoCartao,
  CabecalhoPagina,
  Cartao,
  CorpoCartao,
  Esqueleto,
  TituloCartao,
} from '@/componentes/ui/primitivos';

type Pergunta =
  | 'pico-ocupacao'
  | 'ocupacao-zona'
  | 'ruas-cheias'
  | 'consolidacao'
  | 'saldo-negativo'
  | 'sem-endereco';

const PERGUNTAS: { chave: Pergunta; rotulo: string }[] = [
  { chave: 'pico-ocupacao', rotulo: 'Qual meu pico de ocupação nos últimos 30 dias?' },
  { chave: 'ocupacao-zona', rotulo: 'Como está a ocupação por zona?' },
  { chave: 'ruas-cheias', rotulo: 'Quais corredores estão acima de 90%?' },
  { chave: 'consolidacao', rotulo: 'Onde há oportunidade de consolidação?' },
  { chave: 'saldo-negativo', rotulo: 'Quais posições estão com saldo negativo?' },
  { chave: 'sem-endereco', rotulo: 'Quanto estoque está sem endereço?' },
];

function ehPergunta(valor: string): valor is Pergunta {
  return PERGUNTAS.some((p) => p.chave === valor);
}

/**
 * Análises abre com perguntas, não com parede de gráficos. O resultado
 * escolhe o componente (número, tabela ou série) — e todo resultado exporta.
 *
 * Fora do catálogo abaixo ficou "posições paradas há mais tempo": exigiria
 * dias-na-posição por posição, e /api/mapa não devolve isso (só o detalhe de
 * uma posição por vez devolve). Entra quando a API expuser o campo.
 */
export function Analises(): JSX.Element {
  const { centro, empresa } = useContexto();
  const [perguntaUrl, definirPergunta] = useParamUrl('p');
  const pergunta = ehPergunta(perguntaUrl) ? perguntaUrl : null;

  /*
    Pico do CENTRO ou da EMPRESA.
    Abre na empresa: quem vem a esta tela está perguntando "o galpão está
    enchendo?", e galpão não tem centro — a mesma rua guarda venda, open box e
    reserva. O centro fica a um clique para quem quer olhar um CD específico.
  */
  const [escopoSerie, definirEscopoSerie] = React.useState<'centro' | 'empresa'>('empresa');

  /* `true` = pede a série de 30 dias. Esta é a ÚNICA tela que a desenha, e é a
     única que paga os ~3 s dela — ver `useDashboard`. */
  const dashboard = useDashboard(centro, true);
  /*
    Sem passar a lista de centros: a API soma os que o cadastro marca como
    "contabiliza posições" (Administração > Centros). Recebimento e trânsito
    têm posição, mas é mercadoria de passagem — contá-los aqui daria um pico
    que nunca existiu no chão.
  */
  const serieEmpresa = useSerieEmpresa(pergunta === 'pico-ocupacao' ? empresa : null);
  // O mapa só é necessário nas duas perguntas que olham posição a posição.
  const precisaMapa = pergunta === 'ruas-cheias' || pergunta === 'saldo-negativo';
  const mapa = useMapa(precisaMapa ? centro : null);

  const carregando = dashboard.isLoading || (precisaMapa && mapa.isLoading);

  return (
    <div className="space-y-4 p-3 sm:p-5">
      <CabecalhoPagina
        icone={<BarChart3 className="h-5 w-5" />}
        sobrelinha="Análises"
        titulo="O que você quer saber?"
        descricao="Escolha uma pergunta. O relatório é o resultado dela — não uma tela separada. Todo resultado exporta em CSV."
      />

      <ul className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
        {PERGUNTAS.map((item) => {
          const ativa = pergunta === item.chave;
          return (
            <li key={item.chave}>
              <button
                type="button"
                aria-pressed={ativa}
                onClick={() => definirPergunta(ativa ? null : item.chave)}
                className={`flex min-h-[62px] w-full items-center gap-3 rounded-xl border p-3 text-left text-[13px] font-medium leading-snug shadow-xs transition-all hover:-translate-y-0.5 hover:shadow-cartao ${
                  ativa
                    ? 'border-primaria bg-primaria-suave text-primaria'
                    : 'border-borda bg-superficie hover:border-bordaForte'
                }`}
              >
                <span
                  className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors ${
                    ativa ? 'gradiente-marca text-white' : 'bg-superficie2 text-textoSuave'
                  }`}
                >
                  <Sparkles className="h-4 w-4" />
                </span>
                {item.rotulo}
              </button>
            </li>
          );
        })}
      </ul>

      {pergunta === null ? (
        <Cartao>
          <EstadoVazio
            titulo="Nenhuma pergunta selecionada"
            detalhe="Clique em uma das perguntas acima para ver o resultado."
            icone={<Sparkles className="h-8 w-8" />}
          />
        </Cartao>
      ) : carregando || centro === null ? (
        <Esqueleto className="h-80 w-full" />
      ) : dashboard.isError ? (
        <EstadoErro erro={dashboard.error} aoTentarDeNovo={() => void dashboard.refetch()} />
      ) : precisaMapa && mapa.isError ? (
        <EstadoErro erro={mapa.error} aoTentarDeNovo={() => void mapa.refetch()} />
      ) : (
        <Resultado
          pergunta={pergunta}
          centro={centro}
          empresa={empresa}
          serie={dashboard.data?.serieOcupacao ?? []}
          serieEmpresa={serieEmpresa.data?.serie ?? []}
          estoquesDaEmpresa={serieEmpresa.data?.centros.length ?? 0}
          carregandoSerieEmpresa={serieEmpresa.isLoading}
          escopoSerie={escopoSerie}
          aoTrocarEscopoSerie={definirEscopoSerie}
          zonas={dashboard.data?.ocupacaoPorZona ?? []}
          consolidacao={dashboard.data?.consolidacao ?? []}
          semEndereco={dashboard.data?.resumo?.estoqueSemEndereco ?? 0}
          posicoes={mapa.data?.posicoes ?? []}
        />
      )}
    </div>
  );
}

/** A forma que o painel desenha, venha do centro ou da empresa. */
interface PontoPico {
  data: string;
  ocupados: number;
  total: number;
  percentual: number;
}

interface PropsResultado {
  pergunta: Pergunta;
  centro: number;
  empresa: number | null;
  serie: readonly PontoSerie[];
  serieEmpresa: readonly PontoSerieEmpresa[];
  estoquesDaEmpresa: number;
  carregandoSerieEmpresa: boolean;
  escopoSerie: 'centro' | 'empresa';
  aoTrocarEscopoSerie: (valor: 'centro' | 'empresa') => void;
  zonas: readonly { zona: string; total: number; ocupadas: number; percentual: number }[];
  consolidacao: readonly LinhaConsolidacao[];
  semEndereco: number;
  posicoes: readonly Posicao[];
}

function Resultado(props: PropsResultado): JSX.Element {
  const {
    pergunta,
    centro,
    empresa,
    serie,
    serieEmpresa,
    estoquesDaEmpresa,
    carregandoSerieEmpresa,
    escopoSerie,
    aoTrocarEscopoSerie,
    zonas,
    consolidacao,
    semEndereco,
    posicoes,
  } = props;
  const navegar = useNavigate();
  const sufixo = `${centro}-${carimboArquivo()}`;

  switch (pergunta) {
    case 'pico-ocupacao': {
      /*
        Os dois escopos viram a MESMA forma antes de desenhar: no centro a
        "posição" já é o endereço (um localizador aparece uma vez); na empresa
        o endereço é contado uma vez mesmo servindo a vários centros. Assim o
        gráfico, os destaques e o CSV têm um caminho só.
      */
      const naEmpresa = escopoSerie === 'empresa' && empresa !== null;
      const pontos: PontoPico[] = naEmpresa
        ? serieEmpresa.map((p) => ({
            data: p.data,
            ocupados: p.ocupados,
            total: p.total,
            percentual: p.percentual,
          }))
        : serie.map((p) => ({
            data: p.data,
            ocupados: p.ocupadas,
            total: p.total,
            percentual: p.percentual,
          }));

      const seletor =
        empresa === null ? null : (
          <Segmentado
            valor={escopoSerie}
            aoMudar={aoTrocarEscopoSerie}
            tamanho="pequeno"
            rotuloAcessivel="Pico do centro ou da empresa"
            opcoes={[
              { valor: 'empresa', rotulo: `empresa ${empresa}`, contagem: estoquesDaEmpresa },
              { valor: 'centro', rotulo: `centro ${centro}` },
            ]}
          />
        );

      if (naEmpresa && carregandoSerieEmpresa) {
        return (
          <Cartao>
            <CabecalhoCartao>
              <TituloCartao>Pico de ocupação em 30 dias</TituloCartao>
              {seletor}
            </CabecalhoCartao>
            <CorpoCartao>
              <Esqueleto className="h-80 w-full" />
            </CorpoCartao>
          </Cartao>
        );
      }

      if (pontos.length === 0) {
        return (
          <Cartao>
            <CabecalhoCartao>
              <TituloCartao>Pico de ocupação em 30 dias</TituloCartao>
              {seletor}
            </CabecalhoCartao>
            <CorpoCartao>
              <EstadoVazio
                titulo={
                  naEmpresa
                    ? 'Nenhum estoque desta empresa contabiliza posições'
                    : 'Sem série histórica para este centro'
                }
                {...(naEmpresa
                  ? {
                      detalhe:
                        'Marque "contabiliza posições" em Administração > Centros de estoque.',
                    }
                  : {})}
              />
            </CorpoCartao>
          </Cartao>
        );
      }

      const pico = pontos.reduce((maior, p) => (p.percentual > maior.percentual ? p : maior), pontos[0]!);
      const atual = pontos[pontos.length - 1];
      const unidade = naEmpresa ? 'endereços' : 'posições';
      const colunas: ColunaCsv<PontoPico>[] = [
        { cabecalho: 'Data', valor: (p) => p.data },
        { cabecalho: 'Ocupadas', valor: (p) => p.ocupados },
        { cabecalho: 'Total', valor: (p) => p.total },
        { cabecalho: 'Percentual', valor: (p) => p.percentual },
      ];
      return (
        <Cartao>
          <CabecalhoCartao>
            <TituloCartao>
              Pico de ocupação em 30 dias —{' '}
              {naEmpresa ? `empresa ${empresa}` : `centro ${centro}`}
            </TituloCartao>
            <span className="flex flex-wrap items-center gap-2">
              {seletor}
              <BotaoExportar
                aoExportar={() =>
                  baixarCsv(
                    `ocupacao-30d-${naEmpresa ? `empresa${empresa}` : `centro${centro}`}-${carimboArquivo()}`,
                    pontos,
                    colunas,
                  )
                }
              />
            </span>
          </CabecalhoCartao>
          <CorpoCartao>
            <div className="flex flex-wrap gap-8">
              {/* O contador ao lado do %: 45% de 12.300 e 45% de 12.700 sao a
                  mesma barra e 400 enderecos de diferenca. */}
              <Destaque
                rotulo="Pico"
                valor={percentual(pico.percentual)}
                auxiliar={`${numero(pico.ocupados)} ${unidade} em ${dataCurta(pico.data)}`}
              />
              <Destaque
                rotulo="Último fechamento"
                valor={percentual(atual?.percentual ?? 0)}
                auxiliar={`${numero(atual?.ocupados ?? 0)} de ${numero(atual?.total ?? 0)} ${unidade}`}
              />
              <Destaque
                rotulo="Variação no período"
                valor={`${(atual?.percentual ?? 0) - (pontos[0]?.percentual ?? 0) >= 0 ? '+' : ''}${(
                  (atual?.percentual ?? 0) - (pontos[0]?.percentual ?? 0)
                ).toFixed(1)} p.p.`}
                auxiliar={`${(atual?.ocupados ?? 0) - (pontos[0]?.ocupados ?? 0) >= 0 ? '+' : ''}${numero(
                  (atual?.ocupados ?? 0) - (pontos[0]?.ocupados ?? 0),
                )} ${unidade}`}
              />
            </div>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={pontos} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                  <defs>
                    <linearGradient id="areaOcupacao" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(var(--cor-primaria))" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="rgb(var(--cor-primaria))" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgb(var(--cor-borda))" vertical={false} />
                  <XAxis
                    dataKey="data"
                    tickFormatter={(valor) => dataCurta(String(valor)).slice(0, 5)}
                    tick={{ fontSize: 11, fill: 'rgb(var(--cor-texto-fraco))' }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={24}
                  />
                  <YAxis
                    unit="%"
                    tick={{ fontSize: 11, fill: 'rgb(var(--cor-texto-fraco))' }}
                    tickLine={false}
                    axisLine={false}
                    width={48}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'rgb(var(--cor-superficie))',
                      border: '1px solid rgb(var(--cor-borda))',
                      borderRadius: 6,
                      fontSize: 12,
                      color: 'rgb(var(--cor-texto))',
                    }}
                    labelFormatter={(rotulo) => dataCurta(String(rotulo))}
                    formatter={(valor, _nome, item) => {
                      const p = item?.payload as PontoPico | undefined;
                      return [
                        p
                          ? `${percentual(Number(valor))} · ${numero(p.ocupados)} de ${numero(p.total)}`
                          : percentual(Number(valor)),
                        unidade + ' ocupados',
                      ];
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="percentual"
                    stroke="rgb(var(--cor-primaria))"
                    strokeWidth={2}
                    fill="url(#areaOcupacao)"
                    dot={false}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CorpoCartao>
        </Cartao>
      );
    }

    case 'ocupacao-zona': {
      const colunas: ColunaCsv<(typeof zonas)[number]>[] = [
        { cabecalho: 'Zona', valor: (z) => z.zona },
        { cabecalho: 'Ocupadas', valor: (z) => z.ocupadas },
        { cabecalho: 'Total', valor: (z) => z.total },
        { cabecalho: 'Percentual', valor: (z) => z.percentual },
      ];
      return (
        <TabelaResultado
          titulo="Ocupação por zona"
          vazio={zonas.length === 0}
          aoExportar={() => baixarCsv(`ocupacao-zona-${sufixo}`, zonas, colunas)}
          cabecalhos={['Zona', 'Ocupadas', 'Total', '%']}
          linhas={zonas.map((zona) => ({
            chave: zona.zona,
            celulas: [
              zona.zona,
              numero(zona.ocupadas),
              numero(zona.total),
              percentual(zona.percentual),
            ],
          }))}
        />
      );
    }

    case 'ruas-cheias': {
      const porCorredor = new Map<string, { total: number; ocupadas: number }>();
      for (const posicao of posicoes) {
        const chave = posicao.corredor || '(sem corredor)';
        const atual = porCorredor.get(chave) ?? { total: 0, ocupadas: 0 };
        atual.total += 1;
        if (posicao.status === 'ocupada') atual.ocupadas += 1;
        porCorredor.set(chave, atual);
      }
      const linhas = [...porCorredor.entries()]
        .map(([corredor, v]) => ({
          corredor,
          total: v.total,
          ocupadas: v.ocupadas,
          percentual: v.total > 0 ? (v.ocupadas / v.total) * 100 : 0,
        }))
        .filter((l) => l.percentual >= 90)
        .sort((a, b) => b.percentual - a.percentual || compararNatural(a.corredor, b.corredor));

      const colunas: ColunaCsv<(typeof linhas)[number]>[] = [
        { cabecalho: 'Corredor', valor: (l) => l.corredor },
        { cabecalho: 'Ocupadas', valor: (l) => l.ocupadas },
        { cabecalho: 'Total', valor: (l) => l.total },
        { cabecalho: 'Percentual', valor: (l) => Math.round(l.percentual * 10) / 10 },
      ];
      return (
        <TabelaResultado
          titulo="Corredores acima de 90% de ocupação"
          vazio={linhas.length === 0}
          textoVazio="Nenhum corredor acima de 90% neste centro."
          aoExportar={() => baixarCsv(`ruas-cheias-${sufixo}`, linhas, colunas)}
          cabecalhos={['Corredor', 'Ocupadas', 'Total', '%']}
          linhas={linhas.map((linha) => ({
            chave: linha.corredor,
            aoClicar: () =>
              navegar(`/estoque?centro=${centro}&corredor=${encodeURIComponent(linha.corredor)}`),
            celulas: [
              linha.corredor,
              numero(linha.ocupadas),
              numero(linha.total),
              percentual(linha.percentual),
            ],
          }))}
        />
      );
    }

    case 'consolidacao': {
      const linhas = consolidacao.filter((c) => c.posicoes > 1);
      const colunas: ColunaCsv<LinhaConsolidacao>[] = [
        { cabecalho: 'SKU', valor: (c) => c.produto, texto: true },
        { cabecalho: 'Descricao', valor: (c) => c.descricao },
        { cabecalho: 'Posicoes', valor: (c) => c.posicoes },
        { cabecalho: 'SaldoTotal', valor: (c) => c.saldoTotal },
      ];
      return (
        <TabelaResultado
          titulo="SKUs espalhados — oportunidade de consolidação"
          vazio={linhas.length === 0}
          textoVazio="Nenhum SKU ocupa mais de uma posição neste centro."
          aoExportar={() => baixarCsv(`consolidacao-${sufixo}`, linhas, colunas)}
          cabecalhos={['SKU', 'Descrição', 'Posições', 'Saldo']}
          linhas={linhas.map((linha) => ({
            chave: String(linha.produto),
            /* A linha termina na AÇÃO, não em outro relatório. Ia para o
               Estoque, que mostra o mesmo problema de novo; agora abre as
               Transferências com o SKU pronto para consolidar. */
            aoClicar: () => navegar(`/transferencias?centro=${centro}&sku=${linha.produto}`),
            celulas: [
              String(linha.produto),
              linha.descricao,
              numero(linha.posicoes),
              numero(linha.saldoTotal),
            ],
          }))}
        />
      );
    }

    case 'saldo-negativo': {
      const linhas = posicoes
        .filter((p) => p.status === 'divergencia')
        .sort((a, b) => a.saldo - b.saldo);
      const colunas: ColunaCsv<Posicao>[] = [
        { cabecalho: 'Endereco', valor: (p) => p.descricao },
        { cabecalho: 'Corredor', valor: (p) => p.corredor },
        { cabecalho: 'Zona', valor: (p) => p.zona },
        { cabecalho: 'Saldo', valor: (p) => p.saldo },
      ];
      return (
        <TabelaResultado
          titulo="Posições com saldo negativo"
          vazio={linhas.length === 0}
          textoVazio="Nenhuma posição com saldo negativo. "
          aoExportar={() => baixarCsv(`saldo-negativo-${sufixo}`, linhas, colunas)}
          cabecalhos={['Endereço', 'Corredor', 'Zona', 'Saldo']}
          linhas={linhas.map((linha) => ({
            chave: String(linha.localizador),
            aoClicar: () => navegar(`/movimentos/negativos?centro=${centro}&negativo=${linha.localizador}`),
            celulas: [linha.descricao, linha.corredor, linha.zona, numero(linha.saldo)],
          }))}
        />
      );
    }

    case 'sem-endereco':
      return (
        <Cartao>
          <CabecalhoCartao>
            <TituloCartao>Estoque sem endereçamento</TituloCartao>
          </CabecalhoCartao>
          <CorpoCartao>
            <Destaque
              rotulo="Itens com saldo em LOCALIZADOR = 0"
              valor={numero(semEndereco)}
              auxiliar="existem no estoque, não existem no mapa"
            />
            <p className="mt-4 max-w-prose text-sm text-textoFraco">
              Esse saldo não aparece no mapa porque não tem posição associada. A API devolve
              hoje apenas o total — o detalhamento por SKU depende de uma rota que ainda não
              existe.
            </p>
          </CorpoCartao>
        </Cartao>
      );
  }
}

function Destaque({
  rotulo,
  valor,
  auxiliar,
}: {
  rotulo: string;
  valor: string;
  auxiliar?: string | undefined;
}): JSX.Element {
  return (
    <div>
      <p className="rotulo">{rotulo}</p>
      <p className="tabular mt-1.5 text-[32px] font-bold leading-none tracking-[-0.03em] text-primaria">
        {valor}
      </p>
      {auxiliar ? <p className="mt-1 text-xs text-textoFraco">{auxiliar}</p> : null}
    </div>
  );
}

function BotaoExportar({ aoExportar }: { aoExportar: () => void }): JSX.Element {
  return (
    <Botao variante="secundaria" tamanho="pequeno" onClick={aoExportar}>
      <Download className="h-4 w-4" />
      Exportar
    </Botao>
  );
}

function TabelaResultado({
  titulo,
  cabecalhos,
  linhas,
  vazio,
  textoVazio,
  aoExportar,
}: {
  titulo: string;
  cabecalhos: readonly string[];
  linhas: readonly { chave: string; celulas: readonly string[]; aoClicar?: () => void }[];
  vazio: boolean;
  textoVazio?: string | undefined;
  aoExportar: () => void;
}): JSX.Element {
  return (
    <Cartao>
      <CabecalhoCartao>
        <TituloCartao>{titulo}</TituloCartao>
        {!vazio ? <BotaoExportar aoExportar={aoExportar} /> : null}
      </CabecalhoCartao>
      {vazio ? (
        <EstadoVazio titulo={textoVazio ?? 'Sem resultados'} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-borda bg-superficie2 text-left text-[11px] uppercase tracking-[0.04em] text-textoFraco">
                {cabecalhos.map((cabecalho, i) => (
                  <th key={cabecalho} className={`px-4 py-2.5 font-bold ${i > 0 ? 'text-right' : ''}`}>
                    {cabecalho}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {linhas.map((linha) => (
                <tr
                  key={linha.chave}
                  onClick={linha.aoClicar}
                  className={`border-b border-borda text-[13px] last:border-0 ${
                    linha.aoClicar
                      ? 'cursor-pointer transition-colors hover:bg-primaria-suave/50'
                      : ''
                  }`}
                >
                  {linha.celulas.map((celula, i) => (
                    <td
                      key={`${linha.chave}-${i}`}
                      className={`px-4 py-2.5 ${i > 0 ? 'tabular text-right' : 'font-semibold'}`}
                    >
                      {celula}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Cartao>
  );
}
